import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, friendlyError } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { Button, Callout, Lockup, TextField } from '../components/ui';

function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="auth-wrap">
      <main className="auth-card" id="main">
        <Lockup size={44} />
        <h1>{title}</h1>
        {children}
      </main>
    </div>
  );
}

function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

const LINK_ERRORS: Record<string, string> = {
  otp_expired: 'That email link has expired or was already used. Enter your email below and we’ll send a new one.',
  access_denied: 'That email link couldn’t be used. Enter your email below and we’ll send a new one.',
};

/** Sends a fresh sign-up confirmation email. */
function ResendConfirmation({ initialEmail = '' }: { initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setEmail(initialEmail), [initialEmail]);
  async function send(e: FormEvent) {
    e.preventDefault();
    setState('busy'); setError(null);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/sign-in?verified=1` },
    });
    if (error) { setError(friendlyError(error)); setState('idle'); return; }
    setState('sent');
  }
  if (state === 'sent') return <Callout variant="note" role="status">If that address has an unconfirmed account, a new confirmation link is on its way. Open the newest email.</Callout>;
  return (
    <form className="stack box" onSubmit={send} noValidate style={{ padding: 16 }}>
      {error && <Callout variant="error">{error}</Callout>}
      <TextField label="Email for a new confirmation link" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <Button type="submit" variant="secondary" busy={state === 'busy'} disabled={!email.trim()}>Resend confirmation email</Button>
    </form>
  );
}

export function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const linkError = params.get('link_error');
  const needsConfirmation = Boolean(error && /confirm your email/i.test(error));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) { setError(friendlyError(error)); return; }
    navigate(safeNext(params.get('next')), { replace: true });
  }

  // A confirmation link opened in the same browser signs the learner straight in.
  if (!loading && user) return <Navigate to={safeNext(params.get('next'))} replace />;

  return (
    <AuthCard title="Sign in">
      {params.get('verified') && !linkError && <Callout variant="note">Email confirmed. Sign in to continue.</Callout>}
      {linkError && <Callout variant="error">{LINK_ERRORS[linkError] ?? LINK_ERRORS.access_denied}</Callout>}
      {linkError && <ResendConfirmation />}
      {error && <Callout variant="error">{error}</Callout>}
      {needsConfirmation && <ResendConfirmation initialEmail={email} />}
      <form className="stack" onSubmit={submit} noValidate>
        <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" block busy={busy} disabled={!email || !password}>Sign in</Button>
      </form>
      <p className="small muted" style={{ margin: 0 }}>
        <Link to="/forgot-password">Forgot your password?</Link> · New to BlueLine? <Link to={`/sign-up${params.get('next') ? `?next=${encodeURIComponent(params.get('next')!)}` : ''}`}>Create an account</Link>
      </p>
    </AuthCard>
  );
}

export function SignUp() {
  const [params] = useSearchParams();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const pwError = password && password.length < 10 ? 'Use at least 10 characters.' : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pwError || !fullName.trim()) return;
    setBusy(true); setError(null);
    const next = safeNext(params.get('next'));
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { full_name: fullName.trim() }, emailRedirectTo: `${window.location.origin}/sign-in?verified=1&next=${encodeURIComponent(next)}` },
    });
    setBusy(false);
    if (error) { setError(friendlyError(error)); return; }
    // With email confirmation on, Supabase returns no session until the link is clicked.
    if (data.session) window.location.assign(next);
    else setSent(true);
  }

  if (sent) {
    return (
      <AuthCard title="Check your email">
        <p style={{ margin: 0 }}>We sent a confirmation link to <strong>{email}</strong>. Open it to verify your address, then sign in.</p>
        <p className="small muted" style={{ margin: 0 }}>Didn't get it? Check spam, or send a new link. Only the newest link works.</p>
        <ResendConfirmation initialEmail={email} />
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Create your account">
      {error && <Callout variant="error">{error}</Callout>}
      <form className="stack" onSubmit={submit} noValidate>
        <TextField label="Full name" help="Printed on your certificates exactly as entered." autoComplete="name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" autoComplete="new-password" required value={password} error={pwError} help="At least 10 characters." onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" block busy={busy} disabled={!email || !password || !fullName.trim() || Boolean(pwError)}>Create account</Button>
      </form>
      <p className="small muted" style={{ margin: 0 }}>Already have an account? <Link to="/sign-in">Sign in</Link></p>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/update-password` });
    setBusy(false);
    if (error) setError(friendlyError(error));
    else setDone(true); // Same message whether or not the account exists.
  }

  return (
    <AuthCard title="Reset your password">
      {done ? (
        <p style={{ margin: 0 }}>If an account exists for <strong>{email}</strong>, a reset link is on its way. It works once and expires in 60 minutes.</p>
      ) : (
        <form className="stack" onSubmit={submit} noValidate>
          {error && <Callout variant="error">{error}</Callout>}
          <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" block busy={busy} disabled={!email}>Send reset link</Button>
        </form>
      )}
      <p className="small muted" style={{ margin: 0 }}><Link to="/sign-in">Back to sign in</Link></p>
    </AuthCard>
  );
}

export function UpdatePassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pwError = password && password.length < 10 ? 'Use at least 10 characters.' : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pwError) return;
    setBusy(true); setError(null);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) { setError(friendlyError(error)); return; }
    navigate('/', { replace: true });
  }

  return (
    <AuthCard title="Choose a new password">
      {error && <Callout variant="error">{error}</Callout>}
      <form className="stack" onSubmit={submit} noValidate>
        <TextField label="New password" type="password" autoComplete="new-password" required value={password} error={pwError} help="At least 10 characters." onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" block busy={busy} disabled={!password || Boolean(pwError)}>Save password</Button>
      </form>
    </AuthCard>
  );
}
